import React, { useState } from 'react';
import TickBox from '../components/TickBox';
import PhotoTile from '../components/PhotoTile';
import Drawer from '../components/Drawer';
import PhotoViewer from '../components/PhotoViewer';
import PlaylistPane from './PlaylistPane';
const sameId = (a, b) => String(a) === String(b);

// Signed server-side and expiring, so it is used exactly as given. An empty
// one means the card has no face; nothing is constructed to stand in for it.
const coverOf = option => option.cover_url || '';

// What an option looks like is decided by what the server sent with it, not by
// which section it happens to sit in.
const visualOf = option => {
  if (option.embed_url) return 'embed';
  if ((option.media || []).length || coverOf(option)) return 'photo';
  return 'plain';
};

/** A `list` field that carries options: a capped picker. */
const OptionPicker = ({
  field,
  picked,
  locked,
  isPhone,
  onToggle
}) => {
  const [openId, setOpenId] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(null);
  const options = field.options || [];
  const cap = field.max_picks || 0;
  const atLimit = cap > 0 && picked.length >= cap;
  const isOn = option => picked.some(x => sameId(x, option.id));
  const open = options.find(o => sameId(o.id, openId)) || null;
  const openVisual = open ? visualOf(open) : null;
  const media = open?.media || [];

  // Nothing here has a cover or an embed, so it renders as plain tick cards.
  if (!options.some(o => visualOf(o) !== 'plain')) {
    return React.createElement("div", {
      className: "cp-pickgrid"
    }, options.map(option => {
      const on = isOn(option);
      const blocked = locked || atLimit && !on;
      return React.createElement("button", {
        key: option.id,
        type: "button",
        className: `cp-pick${on ? ' is-on' : ''}${blocked && !on ? ' is-dim' : ''}`,
        disabled: blocked,
        onClick: () => onToggle(option.id)
      }, React.createElement(TickBox, {
        checked: on
      }), React.createElement("span", {
        className: "cp-pick-text"
      }, React.createElement("strong", null, option.name), option.blurb && React.createElement("span", null, option.blurb)));
    }));
  }
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: "cp-cardgrid"
  }, options.map(option => React.createElement(PhotoTile, {
    key: option.id,
    name: option.name,
    cover: coverOf(option),
    showView: visualOf(option) !== 'plain',
    checked: isOn(option),
    atLimit: atLimit,
    locked: locked,
    onOpen: () => setOpenId(option.id),
    onToggle: () => !locked && onToggle(option.id)
  }))), React.createElement(Drawer, {
    open: !!open,
    title: open?.name,
    meta: open?.blurb || (cap ? `${picked.length} / ${cap} ${field.label}` : ''),
    checked: open ? isOn(open) : false,
    tickLabel: open && isOn(open) ? 'Selected' : 'Select',
    locked: locked || atLimit && open && !isOn(open),
    fill: openVisual === 'embed',
    onToggle: () => open && !locked && onToggle(open.id),
    onClose: () => setOpenId(null)
  }, openVisual === 'embed' && React.createElement(PlaylistPane, {
    option: open
  }), openVisual === 'photo' && (media.length ? React.createElement("div", {
    className: "cp-masonry"
  }, media.map((m, i) => React.createElement("button", {
    type: "button",
    className: "cp-masonry-item",
    key: m.url || i,
    onClick: () => setPhotoIndex(i),
    "aria-label": `View reference ${i + 1}`
  }, React.createElement("img", {
    src: m.url,
    alt: "",
    loading: "lazy"
  })))) :
  
  // Ops has not pointed this collection at a source booking yet.
  React.createElement("p", {
    className: "cp-emptymedia"
  }, "No references have been added to this collection yet."))), React.createElement(PhotoViewer, {
    items: media,
    index: photoIndex,
    onIndex: setPhotoIndex,
    isPhone: isPhone,
    onClose: () => setPhotoIndex(null)
  }));
};
export default OptionPicker;
